/**
 * src/shell/portal/SocialPortalView.tsx
 *
 * Minimalist Discovery Portal, Live WebRTC Broadcasts & Sovereign Channel Hubs.
 * - Minimalist streaming-first discovery canvas (Netflix-style shelves & Hero Live Stream).
 * - Frictionless 1-Click "Book a Demo" executive scheduler modal.
 * - Removed enterprise "Switch to Private Mesh" toggle (consumer portal purity).
 * - Live WebRTC broadcasting & private watch parties with pinned garage shop items.
 * - Zero-knowledge Channel Visa age attestation gate.
 * - Creator Garage Shops & Opt-in Contextual Attention Dividends.
 */

import React, { useState, useEffect } from 'react';
import {
  SocialPost,
  SocialChannel,
  ChannelCategory,
  sortPostsChronologically,
  canUserPostToChannel,
} from '../../domain/social/channel-contracts';
import { channelManager } from '../../domain/social/channel-manager';
import { SovereignScoreCard } from './components/SovereignScoreCard';
import { ChannelCreationModal } from './components/ChannelCreationModal';
import { ChannelInviteModal } from './components/ChannelInviteModal';
import { ImageRemoverModal } from './components/ImageRemoverModal';
import { WorkspaceExtensionDrawer } from './components/WorkspaceExtensionDrawer';
import { DemoSchedulerModal } from './components/DemoSchedulerModal';
import { NetflixDiscoveryCanvas } from './components/NetflixDiscoveryCanvas';
import { LiveStreamRoom } from './components/LiveStreamRoom';
import { ChannelVisaGateModal } from './components/ChannelVisaGateModal';
import { GarageShopDrawer, GarageProduct, SEEDED_GARAGE_PRODUCTS } from '../../plugins/commerce/GarageShopDrawer';
import { ContextualAdBanner } from './components/ContextualAdBanner';
import { SponsorVaultChannelView } from './components/SponsorVaultChannelView';
import { JuryChamberModal } from './components/JuryChamberModal';
import { MeshTacticsArena } from './components/MeshTacticsArena';
import { SovereignScoreProfile } from '../../domain/identity/sovereign-score';
import { webrtcBroadcaster, BroadcastSession } from '../../mesh/broadcast/webrtc-broadcaster';
import { channelVisaEngine, VisaType } from '../../domain/identity/channel-visa-engine';
import { AppId } from '../../config/app-entitlements';
import { WebAuthnClient } from '../../domain/identity/webauthn-client';
import { MobileReelsFeed } from './components/MobileReelsFeed';
import { ConsumerOnboardingModal } from './components/ConsumerOnboardingModal';
import { RadicalTransparencyView } from './components/RadicalTransparencyView';
import { RemoteModule } from '../modules/ModuleLoader.js';
import { syncModuleCatalog } from '../modules/ModuleRegistry.js';
import { AppLauncher } from '../components/AppLauncher.js';
import { QuotePostModal } from './components/QuotePostModal';
import { DtnSyncIndicator } from './components/DtnSyncIndicator';
import { wallClient } from './api/WallClient.js';
import {
  Globe,
  Radio,
  Send,
  CheckCircle,
  Share2,
  Flame,
  ShieldCheck,
  Repeat,
  Plus,
  UserPlus,
  Layers,
  Sparkles,
  Calendar,
  ShoppingBag,
  Tv,
  MessageSquare,
  Lock,
  KeyRound,
  Scale,
  Swords,
} from 'lucide-react';

export interface SocialPortalViewProps {
  currentUserDisplayName?: string;
  currentUserHandle?: string;
  onSwitchToWorkspace?: () => void;
  onLaunchEnterpriseApp?: (appId: string) => void;
}

const CATEGORY_TABS: { id: ChannelCategory | 'all'; label: string; icon: string }[] = [
  { id: 'all', label: 'All Channels', icon: '🌐' },
  { id: 'town-square', label: 'Town Square', icon: '🏛️' },
  { id: 'creators-media', label: 'Creators & Media', icon: '🎨' },
  { id: 'tech-depin', label: 'Tech & DePIN', icon: '⚡' },
  { id: 'local-hangouts', label: 'Local Hangouts', icon: '📍' },
  { id: 'private-circles', label: 'Private Circles', icon: '🔒' },
];

const INITIAL_POSTS: SocialPost[] = [
  {
    id: 'post_general_1',
    channelId: 'chan_general',
    authorPersonaId: 'p_admin',
    authorDisplayName: 'Hercules',
    authorHandle: 'hercules.herx',
    authorAvatarUrl: '',
    authorTrustScore: 285,
    contentMarkdown:
      'Welcome to the Town Square! We have transitioned the portal into a clean, streaming-first discovery canvas with WebRTC broadcasts, creator garage drops, and guaranteed 100% organic reach to every follower.',
    mediaAttachments: [],
    endorsementScore: 56,
    reactions: { '🔥': 41, '🛡️': 23, '⚡': 52 },
    remeshCount: 22,
    canonicalHash: '8f72a1bc3e9d0a',
    subscriberReachCount: 8420,
    createdAt: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
  },
  {
    id: 'post_showcase_1',
    channelId: 'chan_showcase',
    authorPersonaId: 'p_creator1',
    authorDisplayName: 'Nadia Chen',
    authorHandle: 'nadiachen.herx',
    authorAvatarUrl: '',
    authorTrustScore: 195,
    contentMarkdown:
      'Going live shortly with our indie watch party and lo-fi sound drop! Pinning the sample pack directly to the WebRTC video stream overlay with 1-click escrow checkout.',
    mediaAttachments: [],
    endorsementScore: 38,
    reactions: { '❤️': 30, '🚀': 21 },
    remeshCount: 12,
    canonicalHash: '2c5d8e9f1a4b',
    subscriberReachCount: 5120,
    createdAt: new Date(Date.now() - 80 * 60 * 1000).toISOString(),
  },
];

export const SocialPortalView: React.FC<SocialPortalViewProps> = ({
  currentUserDisplayName = 'Sovereign Pioneer',
  currentUserHandle = 'pioneer.herx',
  onLaunchEnterpriseApp,
}) => {
  // Top level active tab: 'explore' (Netflix canvas) vs 'feed' (3-column channels & posts) vs 'reels' (mobile full-bleed) vs 'apps'
  const [activeMainTab, setActiveMainTab] = useState<'explore' | 'feed' | 'reels' | 'apps'>('explore');
  const [isConsumerOnboardingOpen, setIsConsumerOnboardingOpen] = useState<boolean>(false);

  useEffect(() => {
    syncModuleCatalog();
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setActiveMainTab('reels');
    }
  }, []);

  // Hash-based routing for #/transparency and #/wall
  const [isTransparencyViewOpen, setIsTransparencyViewOpen] = useState<boolean>(() => {
    return typeof window !== 'undefined' && window.location.hash === '#/transparency';
  });
  const [isMyWallOpen, setIsMyWallOpen] = useState<boolean>(() => {
    return typeof window !== 'undefined' && window.location.hash.startsWith('#/wall');
  });
  const [activeWallUserDid, setActiveWallUserDid] = useState<string | undefined>(() => {
    if (typeof window !== 'undefined' && window.location.hash.startsWith('#/wall/')) {
      return window.location.hash.split('/')[2];
    }
    return undefined;
  });
  const [quoteModalPost, setQuoteModalPost] = useState<SocialPost | null>(null);

  useEffect(() => {
    const handleHash = () => {
      if (typeof window !== 'undefined') {
        setIsTransparencyViewOpen(window.location.hash === '#/transparency');
        const isWall = window.location.hash.startsWith('#/wall');
        setIsMyWallOpen(isWall);
        if (isWall && window.location.hash.startsWith('#/wall/')) {
          setActiveWallUserDid(window.location.hash.split('/')[2]);
        } else if (isWall) {
          setActiveWallUserDid(undefined);
        }
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const [channels, setChannels] = useState<SocialChannel[]>(() => channelManager.getAllChannels());
  const [selectedChannelId, setSelectedChannelId] = useState<string>('chan_general');
  const [selectedCategory, setSelectedCategory] = useState<ChannelCategory | 'all'>('all');
  const [posts, setPosts] = useState<SocialPost[]>(INITIAL_POSTS);
  const [newPostContent, setNewPostContent] = useState<string>('');
  const [postError, setPostError] = useState<string | null>(null);

  // Modals & Drawers
  const [isDemoModalOpen, setIsDemoModalOpen] = useState<boolean>(false);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState<boolean>(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
  const [isExtensionDrawerOpen, setIsExtensionDrawerOpen] = useState<boolean>(false);
  const [isGarageDrawerOpen, setIsGarageDrawerOpen] = useState<boolean>(false);
  const [isVisaModalOpen, setIsVisaModalOpen] = useState<boolean>(false);
  const [gatedChannelPending, setGatedChannelPending] = useState<SocialChannel | null>(null);
  const [isPasskeyAuthenticated, setIsPasskeyAuthenticated] = useState<boolean>(false);
  const [isJuryModalOpen, setIsJuryModalOpen] = useState<boolean>(false);
  const [isArcadeModalOpen, setIsArcadeModalOpen] = useState<boolean>(false);
  const [isImageRemoverOpen, setIsImageRemoverOpen] = useState<boolean>(false);

  // WebRTC Live Broadcast
  const [activeStreamSession, setActiveStreamSession] = useState<BroadcastSession | null>(null);
  const [pinnedProduct, setPinnedProduct] = useState<GarageProduct | undefined>(SEEDED_GARAGE_PRODUCTS[0]);

  // Token-Gated Credits & Unlocked Apps
  const [availableCredits, setAvailableCredits] = useState<number>(350);
  const [unlockedAppIds, setUnlockedAppIds] = useState<Set<string>>(new Set(['taskflow']));

  // Profile data for SovereignScoreCard
  const mockProfile: SovereignScoreProfile = {
    verificationTier: 'passkey',
    avgEndorsementScore: 75,
    incomingVouches: 2,
    totalBytesRelayed: 2.1e9, // 2.1 GB
    juryVotes: 6,
    daysStaked: 25,
  };

  const activeChannel = channels.find((c) => c.id === selectedChannelId) || channels[0];

  // Channels filtering: Adult/mature content is strictly prohibited by hosting policy
  const filteredChannels = (selectedCategory === 'all'
    ? channels
    : channels.filter((c) => c.category === selectedCategory)
  ).filter((c) => !c.isMature);

  const handleChannelCreated = (newChannel: SocialChannel) => {
    setChannels(channelManager.getAllChannels());
    setSelectedChannelId(newChannel.id);
  };

  const handleSelectChannel = (channel: SocialChannel) => {
    if (channel.isMature) {
      alert('Adult and mature content is strictly prohibited by hosting policy.');
      return;
    }

    if (channel.accessLevel === 'invite-only') {
      const access = channelVisaEngine.verifyChannelAccess(channel.id, 'current_user', 'INVITE_SECRET');
      if (!access.hasAccess) {
        setGatedChannelPending(channel);
        setIsVisaModalOpen(true);
        return;
      }
    }
    setSelectedChannelId(channel.id);
  };

  const handleStartBroadcast = async () => {
    try {
      const session = await webrtcBroadcaster.startBroadcast({
        channelId: activeChannel.id,
        broadcasterPersonaId: 'current_user',
        title: `Live in #${activeChannel.slug}`,
        source: 'camera_mic',
        privacy: 'public_channel',
        pinnedProductId: pinnedProduct?.id,
      });
      setActiveStreamSession(session);
    } catch (err: any) {
      alert(err.message || 'Failed to start broadcast.');
    }
  };

  const handleCreatePost = () => {
    setPostError(null);
    if (!newPostContent.trim()) return;

    const evaluation = canUserPostToChannel(activeChannel, 175, 'current_user');
    if (!evaluation.allowed) {
      setPostError(evaluation.reason || 'You do not have permission to post to this channel.');
      return;
    }

    const post: SocialPost = {
      id: `post_${Date.now()}`,
      channelId: activeChannel.id,
      authorPersonaId: 'current_user',
      authorDisplayName: currentUserDisplayName,
      authorHandle: currentUserHandle,
      authorAvatarUrl: '',
      authorTrustScore: 175,
      contentMarkdown: newPostContent,
      mediaAttachments: [],
      endorsementScore: 0,
      reactions: {},
      remeshCount: 0,
      canonicalHash: Math.random().toString(36).slice(2, 12),
      subscriberReachCount: activeChannel.subscriberCount,
      createdAt: new Date().toISOString(),
    };

    setPosts([post, ...posts]);
    setNewPostContent('');
  };

  const handleUnlockApp = (appId: AppId, price: number) => {
    if (availableCredits < price) return;
    setAvailableCredits((prev) => prev - price);
    setUnlockedAppIds((prev) => new Set([...prev, appId]));
  };

  const sortedPosts = sortPostsChronologically(
    posts.filter((p) => p.channelId === selectedChannelId || selectedCategory === 'all'),
    'newest'
  );

  if (isTransparencyViewOpen) {
    return (
      <RadicalTransparencyView
        onBack={() => {
          setIsTransparencyViewOpen(false);
          if (typeof window !== 'undefined' && window.location.hash === '#/transparency') {
            window.location.hash = '';
          }
        }}
      />
    );
  }

  if (isMyWallOpen) {
    return (
      <RemoteModule
        moduleId="org.herx.wall"
        currentViewerDid="did:herx:p_admin"
        targetUserDid={activeWallUserDid}
        onBack={() => {
          setIsMyWallOpen(false);
          if (typeof window !== 'undefined' && window.location.hash.startsWith('#/wall')) {
            window.location.hash = '';
          }
        }}
        onNavigateToUser={(userDid: string) => {
          setActiveWallUserDid(userDid);
          if (typeof window !== 'undefined') {
            window.location.hash = `#/wall/${userDid}`;
          }
        }}
        fallback={
          <div className="flex items-center justify-center p-8 text-slate-400">
            Loading Sovereign Wall module...
          </div>
        }
      />
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        background: '#090d16',
        color: '#f8fafc',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        overflow: 'hidden',
      }}
    >


      {/* Top App Bar */}
      <header style={{ borderBottom: '1px solid #1e293b', background: 'rgba(15, 23, 42, 0.6)', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyItems: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          <div style={{ height: 28, width: 28, borderRadius: 8, background: '#4f46e5', display: 'flex', alignItems: 'center', justifyItems: 'center', color: '#fff', fontWeight: 'bold' }}>
            H
          </div>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Sovereign OS</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => setIsImageRemoverOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'rgba(56, 189, 248, 0.1)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              color: '#38bdf8',
              cursor: 'pointer',
            }}
            title="AI Image Background Remover"
          >
            <Sparkles size={16} />
          </button>

          <nav style={{ display: 'flex', alignItems: 'center', background: 'rgba(2, 6, 23, 0.8)', padding: 4, borderRadius: 8, border: '1px solid #1e293b' }}>
          <button
            onClick={() => setActiveMainTab('apps')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              background: activeMainTab === 'apps' ? '#4f46e5' : 'transparent',
              color: activeMainTab === 'apps' ? '#fff' : '#94a3b8',
            }}
          >
            Modules
          </button>
          <button
            onClick={() => setActiveMainTab('explore')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              background: activeMainTab !== 'apps' ? '#4f46e5' : 'transparent',
              color: activeMainTab !== 'apps' ? '#fff' : '#94a3b8',
            }}
          >
            Wall
          </button>
        </nav>
        </div>
      </header>

      {/* Sovereign Offline DTN Outbox Status Pill */}
      <DtnSyncIndicator />

      {/* ── Main View Content ────────────────────────────────────────────── */}
      {activeMainTab === 'apps' ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <AppLauncher />
        </div>
      ) : activeMainTab === 'reels' ? (
        <MobileReelsFeed
          onExitReels={() => setActiveMainTab('explore')}
          onProductCheckout={(_prodId) => {
            setIsGarageDrawerOpen(true);
          }}
          onSelectChannel={(channelName) => {
            const cleanName = channelName.replace(/^#/, '').toLowerCase();
            const target = channels.find(
              (c) =>
                c.slug.toLowerCase() === cleanName ||
                c.title.toLowerCase().includes(cleanName) ||
                c.id === channelName ||
                c.id === `chan_${cleanName.replace(/-/g, '_')}`
            );
            if (target) {
              handleSelectChannel(target);
            }
            setActiveMainTab('feed');
          }}
        />
      ) : activeMainTab === 'explore' ? (
        <NetflixDiscoveryCanvas
          onWatchLiveHero={() => {
            const mockSession: BroadcastSession = {
              id: 'hero_stream_42',
              channelId: 'chan_node_runners',
              broadcasterPersonaId: 'p_depin',
              title: 'Decentralized Mesh Infrastructure Build #42',
              source: 'camera_mic',
              privacy: 'public_channel',
              viewerCount: 1240,
              relayMode: 'MEDIASOUP_SFU',
              startedAt: Date.now() - 45 * 60 * 1000,
              isActive: true,
              pinnedProductId: 'prod_mech_02',
            };
            setActiveStreamSession(mockSession);
          }}
          onEnterChannel={(channelId) => {
            const cleanName = channelId.replace(/^chan_/, '').replace(/^#/, '').toLowerCase();
            const target = channels.find(
              (c) =>
                c.id === channelId ||
                c.slug.toLowerCase() === cleanName ||
                c.title.toLowerCase().includes(cleanName)
            );
            if (target) {
              handleSelectChannel(target);
            } else {
              setSelectedChannelId(channelId);
            }
            setActiveMainTab('feed');
          }}
          onOpenGarage={() => setIsGarageDrawerOpen(true)}
          onSelectProduct={(p) => {
            setPinnedProduct(p);
            setIsGarageDrawerOpen(true);
          }}
        />
      ) : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Column: Categorized Channels */}
          <aside
            style={{
              width: 300,
              background: '#0c101a',
              borderRight: '1px solid #1e293b',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 16px 8px 16px', borderBottom: '1px solid #1e293b' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: '#64748b', textTransform: 'uppercase' }}>
                  CHANNELS
                </span>
                <button
                  type="button"
                  onClick={() => setIsCreationModalOpen(true)}
                  style={{
                    background: 'rgba(56, 189, 248, 0.15)',
                    border: '1px solid rgba(56, 189, 248, 0.3)',
                    color: '#38bdf8',
                    borderRadius: 6,
                    padding: '3px 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <Plus size={12} /> New
                </button>
              </div>

              {/* Category Filter Chips */}
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6 }}>
                {CATEGORY_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSelectedCategory(tab.id)}
                    style={{
                      background: selectedCategory === tab.id ? '#0284c7' : '#1e293b',
                      color: selectedCategory === tab.id ? '#ffffff' : '#94a3b8',
                      border: 'none',
                      borderRadius: 14,
                      padding: '3px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Channel List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filteredChannels.map((chan) => {
                const isSelected = chan.id === selectedChannelId;

                return (
                  <div
                    key={chan.id}
                    onClick={() => handleSelectChannel(chan)}
                    style={{
                      background: isSelected ? '#162032' : '#0f172a',
                      border: isSelected ? '1px solid #0284c7' : '1px solid #1e293b',
                      borderRadius: 10,
                      padding: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: isSelected ? '#38bdf8' : '#f8fafc' }}>
                        #{chan.slug}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          background: chan.accessLevel === 'invite-only' ? 'rgba(168, 85, 247, 0.15)' : 'rgba(56, 189, 248, 0.1)',
                          color: chan.accessLevel === 'invite-only' ? '#c084fc' : '#38bdf8',
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontWeight: 600,
                        }}
                      >
                        {chan.accessLevel === 'invite-only' ? '🔒 Visa Gated' : chan.accessLevel === 'community-read-only' ? '📢 Broadcast' : '🌐 Public'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.3 }}>
                      {chan.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Center Column: Feed & Composer */}
          <main
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              padding: '20px 24px',
              maxWidth: 720,
              margin: '0 auto',
              gap: 20,
            }}
          >
            {/* Active Channel Header with Broadcast Trigger */}
            <div
              style={{
                background: '#0c101a',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: '#f8fafc' }}>#{activeChannel.slug}</span>
                  <span
                    style={{
                      fontSize: 11,
                      background: 'rgba(56, 189, 248, 0.15)',
                      color: '#38bdf8',
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontWeight: 600,
                    }}
                  >
                    {activeChannel.title}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  {activeChannel.description} &bull; {activeChannel.subscriberCount.toLocaleString()} subscribers
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={handleStartBroadcast}
                  style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#ffffff',
                    padding: '7px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Radio size={13} /> Go Live
                </button>

                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(true)}
                  style={{
                    background: 'rgba(56, 189, 248, 0.1)',
                    border: '1px solid #0284c7',
                    borderRadius: 8,
                    color: '#38bdf8',
                    padding: '7px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <UserPlus size={13} /> Invite
                </button>
              </div>
            </div>

            {activeChannel.id === 'chan_sponsor_vault' ? (
              <SponsorVaultChannelView
                onDividendClaimed={(tokens) => alert(`Received +${tokens} tokens attention dividend!`)}
              />
            ) : (
              <>
                {/* Post Composer */}
                <div
                  style={{
                    background: '#0c101a',
                    border: '1px solid #1e293b',
                    borderRadius: 14,
                    padding: 16,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                  }}
                >
              {postError && (
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', padding: '8px 12px', borderRadius: 8, color: '#fca5a5', fontSize: 12, marginBottom: 12 }}>
                  {postError}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc' }}>
                  Post to #{activeChannel.slug}
                </span>
                <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle size={12} /> 100% Guaranteed Reach
                </span>
              </div>

              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder={`Share an update in #${activeChannel.slug}...`}
                rows={3}
                style={{
                  width: '100%',
                  background: '#070a10',
                  border: '1px solid #1e293b',
                  borderRadius: 8,
                  padding: 12,
                  color: '#f8fafc',
                  fontSize: 14,
                  resize: 'none',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 12 }}>
                <button
                  type="button"
                  onClick={handleCreatePost}
                  disabled={!newPostContent.trim()}
                  style={{
                    background: newPostContent.trim()
                      ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
                      : '#1e293b',
                    color: newPostContent.trim() ? '#ffffff' : '#64748b',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 18px',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: newPostContent.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Send size={14} /> Publish Post
                </button>
              </div>
            </div>

            {/* Chronological Posts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {sortedPosts.map((post) => {
                const channel = channels.find((c) => c.id === post.channelId);

                return (
                  <article
                    key={post.id}
                    style={{
                      background: '#0c101a',
                      border: '1px solid #1e293b',
                      borderRadius: 14,
                      padding: 18,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                            border: '1px solid #334155',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: 15,
                            color: '#38bdf8',
                          }}
                        >
                          {post.authorDisplayName[0]}
                        </div>
                        <div
                          onClick={() => {
                            setActiveWallUserDid(post.authorPersonaId);
                            setIsMyWallOpen(true);
                            if (typeof window !== 'undefined') {
                              window.location.hash = `#/wall/${post.authorPersonaId}`;
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: '#f8fafc' }}>
                              {post.authorDisplayName}
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>@{post.authorHandle}</span>
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            in #{channel?.slug || 'general'} &bull; {new Date(post.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>

                      <div style={{ fontSize: 11, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                        <CheckCircle size={12} /> Delivered to {post.subscriberReachCount.toLocaleString()} subs
                      </div>
                    </div>

                    <div style={{ fontSize: 14, color: '#e2e8f0', lineHeight: 1.6, marginBottom: 14 }}>
                      {post.contentMarkdown}
                    </div>

                    <div
                      style={{
                        borderTop: '1px solid #162032',
                        paddingTop: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: 12,
                        color: '#94a3b8',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 16 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                          <Flame size={14} color="#f59e0b" /> {post.endorsementScore} Endorsements
                        </span>
                        <span
                          onClick={() => setQuoteModalPost(post)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: '#818cf8', fontWeight: 600 }}
                          title="Mirror or Quote to your personal wall"
                        >
                          <Repeat size={14} color="#818cf8" /> Add to My Wall
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                          hash: {post.canonicalHash}
                        </span>
                        <Share2 size={14} style={{ cursor: 'pointer' }} />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </main>

          {/* Right Column: Sovereign Score & Peer Vouching Widget */}
          <aside
            style={{
              width: 320,
              background: '#0c101a',
              borderLeft: '1px solid #1e293b',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              overflowY: 'auto',
            }}
          >
            <SovereignScoreCard
              profile={mockProfile}
              displayName={currentUserDisplayName}
              handle={currentUserHandle}
              onVouchRequest={() => alert('Vouch invitation link copied to clipboard! Share with trusted peers to reach 3x vouches.')}
            />

            {/* Vouching Network Box */}
            <div
              style={{
                background: '#0f172a',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, color: '#f8fafc' }}>
                <ShieldCheck size={16} color="#34d399" />
                Peer Vouching Graph
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                Vouching requires 10 staked tokens. If your vouchee is banned for scams, 50% of your stake is slashed.
              </div>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Active Incoming Vouches:</span>
                  <span style={{ fontWeight: 600, color: '#38bdf8' }}>2 / 3 required</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Tokens Locked in Vouches:</span>
                  <span style={{ fontWeight: 600, color: '#f8fafc' }}>20 tokens</span>
                </div>
              </div>
            </div>

            {/* Community Jury Chamber Card */}
            <div
              style={{
                background: '#0f172a',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, color: '#f8fafc' }}>
                  <Scale size={16} color="#ef4444" />
                  Anti-Scam Jury Chamber
                </div>
                <span style={{ fontSize: 10, background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
                  Active Summons
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, lineHeight: 1.4 }}>
                7-member commit-reveal governance. Review blind evidence and cast sealed votes.
              </div>
              <button
                type="button"
                onClick={() => setIsJuryModalOpen(true)}
                style={{
                  width: '100%',
                  marginTop: 12,
                  background: 'rgba(239, 68, 68, 0.12)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  color: '#f87171',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Scale size={13} /> Enter Jury Chamber
              </button>
            </div>

            {/* Mesh Tactics Arcade Card */}
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.08) 0%, rgba(15, 23, 42, 0.6) 100%)',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                borderRadius: 14,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Swords size={16} color="#38bdf8" />
                <span style={{ fontWeight: 800, fontSize: 13, color: '#f8fafc' }}>
                  Mesh Tactics Arena
                </span>
              </div>
              <p style={{ fontSize: 11, color: '#94a3b8', margin: 0, lineHeight: 1.4 }}>
                Simultaneous 10s commit-reveal lockstep arcade. Attested live by RFC 9591 FROST consensus sentinels.
              </p>
              <button
                type="button"
                onClick={() => setIsArcadeModalOpen(true)}
                style={{
                  width: '100%',
                  marginTop: 12,
                  background: 'rgba(56, 189, 248, 0.12)',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  color: '#38bdf8',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Swords size={13} /> Launch Tactical Arena
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ── Modals & Drawers ──────────────────────────────────────────────── */}
      <MeshTacticsArena
        isOpen={isArcadeModalOpen}
        onClose={() => setIsArcadeModalOpen(false)}
      />
      <JuryChamberModal
        isOpen={isJuryModalOpen}
        onClose={() => setIsJuryModalOpen(false)}
      />
      <DemoSchedulerModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
        currentUserDisplayName={currentUserDisplayName}
        currentUserHandle={currentUserHandle}
      />

      <ChannelCreationModal
        isOpen={isCreationModalOpen}
        onClose={() => setIsCreationModalOpen(false)}
        onChannelCreated={handleChannelCreated}
        creatorPersonaId="current_user"
        creatorDisplayName={currentUserDisplayName}
        creatorHandle={currentUserHandle}
      />

      <ChannelInviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        channel={activeChannel}
        creatorPersonaId="current_user"
      />

      <GarageShopDrawer
        isOpen={isGarageDrawerOpen}
        onClose={() => setIsGarageDrawerOpen(false)}
        channelId={activeChannel.id}
        channelTitle={activeChannel.title}
        onPinToStream={(prod) => {
          setPinnedProduct(prod);
          webrtcBroadcaster.setPinnedProduct(prod.id);
          setIsGarageDrawerOpen(false);
          alert(`Pinned "${prod.title}" to active stream overlay!`);
        }}
      />

      <WorkspaceExtensionDrawer
        isOpen={isExtensionDrawerOpen}
        onClose={() => setIsExtensionDrawerOpen(false)}
        unlockedAppIds={unlockedAppIds}
        availableCredits={availableCredits}
        onUnlockApp={handleUnlockApp}
        onLaunchApp={(appId) => {
          if (onLaunchEnterpriseApp) {
            onLaunchEnterpriseApp(appId);
          }
        }}
      />

      {gatedChannelPending && (
        <ChannelVisaGateModal
          isOpen={isVisaModalOpen}
          onClose={() => {
            setIsVisaModalOpen(false);
            setGatedChannelPending(null);
          }}
          channelId={gatedChannelPending.id}
          channelTitle={gatedChannelPending.title}
          requiredVisaType="private_alpha"
          holderPersonaId="current_user"
          onVisaGranted={() => {
            setSelectedChannelId(gatedChannelPending.id);
            setGatedChannelPending(null);
          }}
        />
      )}

      {activeStreamSession && (
        <LiveStreamRoom
          session={activeStreamSession}
          onClose={() => setActiveStreamSession(null)}
          pinnedProduct={pinnedProduct}
          onBuyProduct={(prod) => {
            alert(`Purchase locked in escrow for: ${prod.title} (${prod.price.currency === 'USD' ? `$${prod.price.amount}` : `${prod.price.amount} TOKENS`})`);
          }}
        />
      )}

      {isConsumerOnboardingOpen && (
        <ConsumerOnboardingModal
          onSuccess={(_userData) => {
            setIsConsumerOnboardingOpen(false);
            setIsPasskeyAuthenticated(true);
            if (_userData.selectedChannels && _userData.selectedChannels.length > 0) {
              const firstChanSlug = _userData.selectedChannels[0].replace(/^#/, '').toLowerCase();
              const found = channels.find((c) => c.slug.toLowerCase() === firstChanSlug);
              if (found) {
                setSelectedChannelId(found.id);
              }
            }
          }}
          onClose={() => setIsConsumerOnboardingOpen(false)}
        />
      )}
      {quoteModalPost && (
        <QuotePostModal
          sourcePost={quoteModalPost}
          sourceChannelName={
            channels.find((c) => c.id === quoteModalPost.channelId)?.title || '#general'
          }
          isOpen={Boolean(quoteModalPost)}
          onClose={() => setQuoteModalPost(null)}
          onSubmitReshare={({ isQuote, commentary }) => {
            wallClient.createPost('did:herx:p_admin', {
              sourcePost: quoteModalPost as any,
              sourceChannelId:
                channels.find((c) => c.id === quoteModalPost.channelId)?.slug || 'general',
              isQuote,
              commentary,
            }).catch(() => {});
            alert('Post successfully added to your Sovereign Wall!');
          }}
        />
      )}
      <ImageRemoverModal 
        isOpen={isImageRemoverOpen} 
        onClose={() => setIsImageRemoverOpen(false)} 
      />
    </div>
  );
};
